import {
  availabilitySchema,
  eligibilitySchema,
  enrollmentResultSchema,
  type AgendaActivity,
  type Availability,
  type EligibilityItem,
  type EnrollmentResult,
} from '@koderek/shared';
import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api, apiGet } from '../../api/client';

/** ADR 05: odświeżanie liczby miejsc co 5 s (US-507 AC-1 – nie rzadziej niż co 10 s); w tle wstrzymane (AC-3). */
export const AVAILABILITY_POLL_MS = 5_000;

export function useAvailability(slug: string, enabled = true) {
  return useQuery({
    queryKey: ['availability', slug],
    queryFn: () => apiGet(`/editions/${encodeURIComponent(slug)}/availability`, availabilitySchema),
    refetchInterval: AVAILABILITY_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    enabled,
  });
}

/** Agenda z aktualną liczbą miejsc z odpytywania (dane treści z agendy, liczby z dostępności). */
export function withAvailability(activities: AgendaActivity[], availability: Availability | undefined): AgendaActivity[] {
  if (!availability) return activities;
  const byId = new Map(availability.items.map((i) => [i.activityId, i]));
  return activities.map((a) => {
    const live = byId.get(a.id);
    return live ? { ...a, enrolled: live.enrolled, capacity: live.capacity, reserve: live.reserve } : a;
  });
}

export interface EnrollmentControls {
  personName: string;
  items: Map<string, EligibilityItem>;
  pendingActivityId: string | null;
  error: unknown;
  enroll: (activity: AgendaActivity) => void;
  unenroll: (activity: AgendaActivity) => void;
}

/** US-507 AC-2: po własnym zapisie lub wypisie stan zmienia się od razu, bez czekania na odpytanie. */
function applyResult(client: QueryClient, slug: string, base: string | null, version: string | undefined, result: EnrollmentResult) {
  client.setQueryData(['eligibility', base, version], result.eligibility);
  client.setQueryData<Availability>(['availability', slug], (old) =>
    old
      ? { ...old, items: old.items.map((i) => (i.activityId === result.availability.activityId ? result.availability : i)) }
      : old,
  );
  void client.invalidateQueries({ queryKey: ['availability', slug] });
}

export interface FamilyEnrollments {
  /** osoba → zajęcia → stan; null, dopóki nie przyjdą dane wszystkich osób. */
  items: Map<string, Map<string, EligibilityItem>> | null;
  pending: { participantId: string; activityId: string } | null;
  error: unknown;
  /** Oś czasu rodziny: zapis z wolnego okna osoby. */
  enroll: (participantId: string, activity: AgendaActivity) => void;
  unenroll: (participantId: string, activity: AgendaActivity) => void;
}

/**
 * Zapisy wszystkich osób naraz (zakładka „Wszyscy” w „Zajęciach rodziny”). Klucze te same co w `useEnrollment`, więc
 * przełączanie między „Wszyscy” a osobą korzysta z pamięci podręcznej, a wypis tu od razu widać w zakładce osoby.
 */
export function useFamilyEnrollments(slug: string, participantIds: string[], version: string | undefined, enabled = true): FamilyEnrollments {
  const client = useQueryClient();
  const base = (id: string) => `/editions/${encodeURIComponent(slug)}/participants/${id}`;
  const queries = useQueries({
    queries: participantIds.map((id) => ({
      queryKey: ['eligibility', base(id), version],
      queryFn: () => apiGet(`${base(id)}/eligibility`, eligibilitySchema),
      enabled: enabled && !!version,
      placeholderData: keepPreviousData,
    })),
  });
  type Vars = { participantId: string; activity: AgendaActivity };
  const enroll = useMutation({
    mutationFn: ({ participantId, activity }: Vars) =>
      api('POST', `${base(participantId)}/enrollments`, { body: { activityId: activity.id }, schema: enrollmentResultSchema }),
    onSuccess: (result, { participantId }) => applyResult(client, slug, base(participantId), version, result),
    onError: () => void client.invalidateQueries({ queryKey: ['availability', slug] }),
  });
  const unenroll = useMutation({
    mutationFn: ({ participantId, activity }: Vars) =>
      api('DELETE', `${base(participantId)}/enrollments/${activity.id}`, { schema: enrollmentResultSchema }),
    onSuccess: (result, { participantId }) => applyResult(client, slug, base(participantId), version, result),
  });
  const pending = enroll.isPending ? enroll.variables : unenroll.isPending ? unenroll.variables : null;
  const ready = queries.every((q) => q.data);
  return {
    items: ready
      ? new Map(participantIds.map((id, i) => [id, new Map(queries[i]!.data!.items.map((item) => [item.activityId, item]))]))
      : null,
    pending: pending ? { participantId: pending.participantId, activityId: pending.activity.id } : null,
    error: queries.find((q) => q.error)?.error ?? enroll.error ?? unenroll.error,
    enroll: (participantId, activity) => {
      unenroll.reset();
      enroll.mutate({ participantId, activity });
    },
    unenroll: (participantId, activity) => {
      enroll.reset();
      unenroll.mutate({ participantId, activity });
    },
  };
}

/**
 * US-501…US-503: zapis i wypis wybranej osoby. `base` to `/editions/{slug}/participants/{id}` (opiekun) albo
 * `/staff/accounts/{konto}/editions/{slug}/participants/{id}` (obsługa, US-505) – reguły są te same.
 */
export function useEnrollment(slug: string, base: string | null, personName: string, version: string | undefined) {
  const client = useQueryClient();
  const eligibility = useQuery({
    queryKey: ['eligibility', base, version],
    queryFn: () => apiGet(`${base}/eligibility`, eligibilitySchema),
    enabled: !!base && !!version,
    placeholderData: keepPreviousData,
  });

  const apply = (result: EnrollmentResult) => applyResult(client, slug, base, version, result);
  const enroll = useMutation({
    mutationFn: (activity: AgendaActivity) =>
      api('POST', `${base}/enrollments`, { body: { activityId: activity.id }, schema: enrollmentResultSchema }),
    onSuccess: apply,
    // AC-3: odrzucony zapis – odświeżamy stan, żeby kafelek pokazał aktualny powód.
    onError: () => void client.invalidateQueries({ queryKey: ['availability', slug] }),
  });
  const unenroll = useMutation({
    mutationFn: (activity: AgendaActivity) => api('DELETE', `${base}/enrollments/${activity.id}`, { schema: enrollmentResultSchema }),
    onSuccess: apply,
  });

  const controls: EnrollmentControls | null =
    base && eligibility.data
      ? {
          personName,
          items: new Map(eligibility.data.items.map((i) => [i.activityId, i])),
          pendingActivityId: enroll.isPending ? enroll.variables.id : unenroll.isPending ? unenroll.variables.id : null,
          error: enroll.error ?? unenroll.error,
          enroll: (activity) => {
            unenroll.reset();
            enroll.mutate(activity);
          },
          unenroll: (activity) => {
            enroll.reset();
            unenroll.mutate(activity);
          },
        }
      : null;
  return { controls, eligibility };
}
